import { useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import {
  NameLink,
  TableRow,
  TableWrap,
  CustomCard,
  PageHeading,
  HeaderColumn,
  BootstrapTable,
  TableDetailCell,
  LastHeaderColumn,
  FirstHeaderColumn
} from "../common-styling";
import {
  fetchMissingInfoCount,
  fetchMissingAdAccountInfo,
  updateMissingAdAccountInfo
} from "../../services/api-declaration";
import TableLoader from "../loader/table-loader";
import NoResultsRow from "../no-results-table-row";
import TextField from "../reusable-form-fields/text-field";
import CustomPagination from "../pagination/custom-pagination";
import { setMissingInfoCount } from "../../store/slices/missingInfo";
import { showError, showSuccess } from "../../services/toast-service";
import { useTranslation } from "react-i18next";

const MissingFunnelAdAccountInfo = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [btnLoading, setBtnLoading] = useState({});
  const [funnels, setFunnels] = useState([]);
  const [paginateOpts, setPaginateOpts] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const [animateItem, setAnimateItem] = useState("");
  const dispatch = useDispatch();

  const handleQueryParams = (property, value) => {
    searchParams.set(property, value);
    setSearchParams(searchParams);
  };
  const methods = useForm({
    defaultValues: {}
  });
  const { register, watch } = methods;

  const handleSave = async (funnelId, key) => {
    const loadingState = btnLoading;
    loadingState[key] = "load";
    setBtnLoading({ ...loadingState });
    const advertisingAccountLink = watch(key);
    try {
      const body = {
        funnelId,
        advertisingAccountLink
      };
      const response = await updateMissingAdAccountInfo(body);
      if (response?.data) {
        setAnimateItem(funnelId);
      }
      showSuccess(response.data.message);
    } catch (error) {
      showError(
        error?.response?.data?.message ||
          t("toastMessage.errorInGetting", {
            what: t("common.funnel")
          })
      );
    } finally {
      loadingState[key] = "hide";
      setBtnLoading({ ...loadingState });
      try {
        const resp = await fetchMissingInfoCount();
        const countData = resp.data.data;
        dispatch(setMissingInfoCount(countData));
      } catch (error) {
        console.log("error", error);
      }
    }
  };

  useEffect(() => {
    const handleFetchMissingAdAccountInfo = async () => {
      try {
        setIsLoading(true);
        const params = {};
        for (const [key, value] of searchParams.entries()) {
          params[key] = value;
        }
        const response = await fetchMissingAdAccountInfo(params);
        const respData = response.data.data;
        if (!respData.data.length) {
          return;
        }
        setFunnels(respData.data);
        setPaginateOpts({
          ...respData.metadata,
          rowsOnPage: respData.data.length
        });
      } catch (error) {
        showError(
          error?.response?.data?.message ||
            t("toastMessage.errorInGetting", {
              what: t("common.funnel", { count: 2 })
            })
        );
      } finally {
        setIsLoading(false);
      }
    };
    handleFetchMissingAdAccountInfo();
    // eslint-disable-next-line
  }, [searchParams]);

  useEffect(() => {
    if (animateItem) {
      setTimeout(() => {
        setPaginateOpts((prevPage) => ({
          ...prevPage,
          totalDocs: prevPage.totalDocs - 1,
          rowsOnPage: prevPage.rowsOnPage - 1
        }));
        setFunnels((prevFunnels) => {
          console.log("prevFunnels ", prevFunnels);
          return prevFunnels.filter((funnel) => funnel.id !== animateItem);
        });
      }, 300);
    }
  }, [animateItem]);

  return (
    <>
      <div>
        <PageHeading className="">{t("Missing Funnel Info")}</PageHeading>
        <CustomCard className="mt-4 mb-3 p-3 rounded-0">
          <FormProvider {...methods}>
            <Form>
              <TableWrap>
                <BootstrapTable responsive>
                  <thead>
                    <tr>
                      <FirstHeaderColumn>
                        Funnel {t("common.name")}
                      </FirstHeaderColumn>
                      <HeaderColumn>
                        {t("funnelIntegration.advertisingAccount.heading")}
                      </HeaderColumn>
                      <LastHeaderColumn />
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <TableLoader colSpan={8} />
                    ) : funnels.length ? (
                      funnels.map((item) => {
                        const key = `advertisingAccountLink-${item._id}`;
                        return (
                          <TableRow
                            className={
                              animateItem === item._id ? "slide-out" : ""
                            }
                            key={key}
                          >
                            <TableDetailCell className="py-3">
                              <NameLink
                                className="d-block"
                                to={`/dashboard/funnels/${item._id}/form`}
                              >
                                {item.general.funnelName}
                              </NameLink>
                            </TableDetailCell>
                            <TableDetailCell
                              style={{ overflow: "visible" }}
                              className="py-3"
                            >
                              <TextField
                                type="text"
                                name={key}
                                placeholder={t("adLinkPlaceholder")}
                                register={register}
                                noSpacing
                              />
                            </TableDetailCell>
                            <TableDetailCell className="text-center align-middle py-3">
                              {btnLoading[key] === "hide" ? (
                                <></>
                              ) : (
                                <Button
                                  disabled={
                                    !watch(key) || btnLoading[key] === "load"
                                  }
                                  variant="primary"
                                  onClick={() => handleSave(item._id, key)}
                                >
                                  {btnLoading[key] === "load"
                                    ? "Saving..."
                                    : "Save"}
                                </Button>
                              )}
                            </TableDetailCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <NoResultsRow lengthOfColumns="5" />
                    )}
                  </tbody>
                </BootstrapTable>
              </TableWrap>
            </Form>
          </FormProvider>
        </CustomCard>
        {Object.values(paginateOpts).length ? (
          <CustomPagination
            paginateOpts={paginateOpts}
            activeTab={Number(searchParams.get("pageIndex")) || 1}
            select={(val) => handleQueryParams("pageIndex", val)}
          />
        ) : (
          <></>
        )}
      </div>
    </>
  );
};

export default MissingFunnelAdAccountInfo;
